import { ChevronDown, Lock, Pencil, Plus, Unlock } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Toggle } from '@/components/ui/toggle'
import { cn } from '@/components/ui/utils'
import { isStageBody, useEditorStore } from '@/stores/editorStore'
import type { Facing, FieldDef, KindDef, StageBody } from '@/schema'

import { openChangeTo, TARGET_HINT, TARGET_ICON, targetLabel, useChangeTargets } from './changeTo'
import { KindIcon, useKindUi } from './KindPicker'
import {
  describeKind,
  DOMAIN_WORDS,
  domainOfFeature,
  domainOfSelection,
  featuresOf,
  kindColour,
  kindLabel,
  PORTAL_EDGES,
  prettyId,
  setFeatureFields,
  setFeatureProp,
  useVocab,
  type StageDomain,
} from './vocab'

/**
 * Details of the selected thing (§3.2): what it is (any kind from the live list, searchable, or a
 * new one), the kind's own extra fields (stored in the feature's `props`), the per-thing settings
 * of its list — walk / block / walk-behind for shapes, facing and room for spots, where it is and
 * how characters go through for ways in and out, looping for paths — and where it came from.
 *
 * Every control says what it does in plain words; "Usual" always means "whatever this kind
 * normally does", which is what an unset field means to the engine.
 */

const FACINGS: Facing[] = ['left', 'right', 'camera', 'away', '3q_left', '3q_right']

const FACING_LABEL: Record<Facing, string> = {
  left: 'Facing left',
  right: 'Facing right',
  camera: 'Facing us',
  away: 'Facing away',
  '3q_left': 'Half-turned left',
  '3q_right': 'Half-turned right',
}

const SOURCE_LABEL: Record<string, string> = {
  seed: 'From the picture file',
  heuristic: 'Guessed by the studio',
  ai: 'Suggested by the computer',
  human: 'Drawn by you',
}

const TRANSITION_LABEL: Record<string, string> = {
  walk_off: 'They walk out of the picture',
  door_open: 'It opens like a door',
  fade: 'The picture fades away',
  cut: 'The story jumps straight there',
}

const EDGE_LABEL: Record<string, string> = { left: 'Left edge', right: 'Right edge', top: 'Top edge', bottom: 'Bottom edge' }

/**
 * Leaving a field ends its undo step: typing in one go is one step, and coming back to the field
 * later starts a new one.
 */
const endEditSession = (): void => useEditorStore.getState().breakCoalescing()

export interface PropertiesPanelProps {
  className?: string
}

type Kinded = StageBody['regions'][number] | StageBody['anchors'][number] | StageBody['portals'][number] | StageBody['paths'][number]

export function PropertiesPanel({ className }: PropertiesPanelProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const selection = useEditorStore((s) => s.selection)
  const setLocked = useEditorStore((s) => s.setLocked)
  const openPicker = useKindUi((s) => s.openPicker)
  const openNewKind = useKindUi((s) => s.openNewKind)
  const openEditKind = useKindUi((s) => s.openEditKind)

  const id = selection.ids[0]
  const domain = domainOfFeature(doc, id)
  const feature = useMemo<Kinded | undefined>(() => (domain && id ? featuresOf(doc, domain).find((f) => f.id === id) : undefined), [doc, domain, id])

  if (!feature || !domain || !isStageBody(doc)) {
    return (
      <div className={className}>
        <p className="p-4 text-sm text-ink-2">Pick something on the picture (or in Everything) to see what it is and change it.</p>
      </div>
    )
  }

  const words = DOMAIN_WORDS[domain]
  const multiDomain = domainOfSelection(doc, selection.ids)
  const many = selection.ids.length > 1
  const sameList = many && multiDomain === domain
  const kindDef = vocab.get(feature.kind, domain)
  const unknownKind = !kindDef
  const hiddenKind = kindDef ? !kindDef.enabled : false
  const record = feature as unknown as Record<string, unknown>
  const locked = feature.provenance.locked === true
  const pickIds = sameList ? selection.ids : [feature.id]

  return (
    <div className={className} data-testid="properties-panel" data-domain={domain}>
      <div className="space-y-4 p-4">
        <header className="flex min-w-0 items-center gap-2">
          <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-line" style={{ background: kindColour(vocab, feature.kind, domain) }} aria-hidden>
            <KindIcon name={kindDef?.icon} className="size-4 text-ink" />
          </span>
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">
            {sameList ? `${selection.ids.length} ${words.many} selected` : ((record['label'] as string | undefined) ?? kindLabel(vocab, feature.kind, domain))}
          </h3>
          <Toggle pressed={locked} onPressedChange={(v) => setLocked(feature.id, v)} aria-label={locked ? 'Unlock this' : 'Lock this'} title={locked ? 'Yours: a re-run will not change it' : 'Lock it so a re-run will not change it'}>
            {locked ? <Lock className="size-4" /> : <Unlock className="size-4" />}
          </Toggle>
        </header>

        {many && !sameList ? <p className="text-xs text-ink-3">Several different things are selected — showing the first.</p> : null}

        <Field label="What is it?">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => openPicker(domain, { kind: 'set', ids: pickIds })}
              className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border border-line bg-paper px-3 text-left text-sm text-ink hover:border-line-strong focus-visible:focus-ring"
              aria-label={`What is it: ${kindLabel(vocab, feature.kind, domain)}. Change`}
              data-testid="kind-trigger"
            >
              <span className="size-3 shrink-0 rounded-full border border-line" style={{ background: kindColour(vocab, feature.kind, domain) }} aria-hidden />
              <span className="min-w-0 flex-1 truncate">{kindLabel(vocab, feature.kind, domain)}</span>
              <ChevronDown className="size-4 shrink-0 text-ink-3" aria-hidden />
            </button>
            {kindDef ? (
              <Button variant="ghost" size="icon" aria-label={`Edit the “${kindLabel(vocab, feature.kind, domain)}” kind`} title="Edit this kind (name, colour, what it does)" onClick={() => openEditKind(domain, kindDef.id)}>
                <Pencil aria-hidden />
              </Button>
            ) : null}
          </div>
        </Field>

        {unknownKind ? (
          <div className="space-y-2 rounded-xl bg-warn-soft p-3 text-sm text-ink" role="note">
            <p className="break-words">“{prettyId(feature.kind)}” is not in your list of {words.many}. It still saves, but stories cannot use it.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" className="min-w-0 max-w-full" onClick={() => openNewKind(domain, { kind: 'set', ids: pickIds }, prettyId(feature.kind))}>
                <Plus aria-hidden /> <span className="truncate">Add “{prettyId(feature.kind)}” to the list</span>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openPicker(domain, { kind: 'set', ids: pickIds })}>
                Choose another
              </Button>
            </div>
          </div>
        ) : (
          <p className="-mt-2 text-xs text-ink-3">
            {describeKind(kindDef).join(' ')}
            {hiddenKind ? ' (This kind is hidden from the lists.)' : ''}
          </p>
        )}

        {/* Every selected thing, like the row menu — even when they are of different lists. */}
        <ChangeTo ids={selection.ids} />

        {sameList ? null : (
          <>
            {/* Ways in and out have no name in the stage schema (it would fail the save). */}
            {domain !== 'portal_kind' ? (
              <Field label="Name (optional)" hint="Only for you — the story never reads it.">
                <Input
                  value={String(record['label'] ?? '')}
                  onChange={(e) => setFeatureFields(feature.id, { label: e.target.value || undefined }, `field:${feature.id}:label`)}
                  onBlur={endEditSession}
                  placeholder={kindLabel(vocab, feature.kind, domain)}
                  fieldSize="sm"
                />
              </Field>
            ) : null}

            {domain === 'region_kind' ? <ShapeSettings feature={feature as StageBody['regions'][number]} kind={kindDef} /> : null}
            {domain === 'anchor_kind' ? <SpotSettings feature={feature as StageBody['anchors'][number]} kind={kindDef} /> : null}
            {domain === 'portal_kind' ? <WaySettings feature={feature as StageBody['portals'][number]} kind={kindDef} doc={doc} /> : null}
            {domain === 'path_kind' ? <PathSettings feature={feature as StageBody['paths'][number]} /> : null}

            {(kindDef?.fields ?? []).length ? (
              <section aria-label={`About this ${words.one}`} className="space-y-3 border-t border-line pt-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">About this {kindLabel(vocab, feature.kind, domain).toLowerCase()}</h4>
                {(kindDef?.fields ?? []).map((f) => (
                  <KindField key={f.key} field={f} value={feature.props?.[f.key]} onChange={(v) => setFeatureProp(feature.id, f.key, v)} />
                ))}
              </section>
            ) : null}
          </>
        )}

        <section aria-label="Where this came from" className="space-y-1 border-t border-line pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">Where this came from</h4>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={feature.provenance.source === 'human' ? 'ok' : feature.provenance.source === 'ai' ? 'info' : 'neutral'}>
              {SOURCE_LABEL[feature.provenance.source] ?? feature.provenance.source}
            </Badge>
            <Badge tone="neutral">{Math.round(feature.provenance.confidence * 100)}% sure</Badge>
            {locked ? (
              <Badge tone="warn" className="max-w-full whitespace-normal">
                Yours — a re-run will not touch it
              </Badge>
            ) : null}
          </div>
          <p className="text-xs text-ink-3">{new Date(feature.provenance.at).toLocaleString()}</p>
        </section>

        {locked ? null : (
          <Button variant="outline" size="sm" onClick={() => setLocked(feature.id, true)}>
            Keep this exactly as it is
          </Button>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Per-list settings                                                           */
/* -------------------------------------------------------------------------- */

function roles(kind: KindDef | undefined): string[] {
  const r = (kind?.semantics as { roles?: unknown } | undefined)?.roles
  return Array.isArray(r) ? (r as string[]) : []
}

function requires(kind: KindDef | undefined): string[] {
  const r = (kind?.semantics as { requires?: unknown } | undefined)?.requires
  return Array.isArray(r) ? (r as string[]) : []
}

/** Usual / Yes / No for an optional boolean override. */
function TriState({ label, hint, value, usual, onChange }: { label: string; hint?: string; value: boolean | undefined; usual: boolean; onChange(v: boolean | undefined): void }) {
  const opts: { v: boolean | undefined; text: string }[] = [
    { v: undefined, text: `Usual (${usual ? 'yes' : 'no'})` },
    { v: true, text: 'Yes' },
    { v: false, text: 'No' },
  ]
  return (
    <Field label={label} hint={hint}>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-[1.6fr_1fr_1fr] gap-0.5 rounded-lg bg-paper-2 p-0.5">
        {opts.map((o) => (
          <button
            key={String(o.v)}
            type="button"
            role="radio"
            aria-checked={value === o.v}
            onClick={() => onChange(o.v)}
            className={cn('truncate rounded-md px-2 py-1.5 text-xs font-medium focus-visible:focus-ring', value === o.v ? 'bg-paper text-ink shadow-card' : 'text-ink-2 hover:text-ink')}
          >
            {o.text}
          </button>
        ))}
      </div>
    </Field>
  )
}

function PctInput({ label, hint, value, onChange }: { label: string; hint?: string; value: number | undefined; onChange(v: number | undefined): void }) {
  return (
    <Field label={label} hint={hint}>
      <Input
        type="number"
        min={0}
        max={100}
        step={0.5}
        fieldSize="sm"
        value={value === undefined ? '' : String(value)}
        onChange={(e) => {
          const n = e.target.value === '' ? undefined : Number(e.target.value)
          onChange(n === undefined || !Number.isFinite(n) ? undefined : Math.min(100, Math.max(0, n)))
        }}
        onBlur={endEditSession}
      />
    </Field>
  )
}

function ShapeSettings({ feature, kind }: { feature: StageBody['regions'][number]; kind: KindDef | undefined }) {
  const r = roles(kind)
  const req = requires(kind)
  const set = (field: string, v: unknown) => setFeatureFields(feature.id, { [field]: v }, `field:${feature.id}:${field}`)
  const occludes = feature.occludes ?? (r.includes('occlude') || r.includes('hide'))
  const needsBaseline = occludes || req.includes('baseline_y') || feature.baseline_y !== undefined
  const needsElevation = req.includes('elevation') || feature.elevation !== undefined
  return (
    <>
      <TriState label="Can characters walk here?" value={feature.walkable} usual={r.includes('walk')} onChange={(v) => set('walkable', v)} />
      <TriState label="Does it block the way?" value={feature.blocks} usual={r.includes('block')} onChange={(v) => set('blocks', v)} />
      <TriState
        label="Do characters walk behind it?"
        hint="Yes for a tree or a sofa: whoever stands higher up the picture than its ground line goes behind it."
        value={feature.occludes}
        usual={r.includes('occlude') || r.includes('hide')}
        onChange={(v) => set('occludes', v)}
      />
      {needsBaseline ? (
        <PctInput
          label="Where it touches the ground"
          hint="How far down the picture (0 = top, 100 = bottom). Characters above this line pass behind it. Or drag the dashed line with the Walk-behind tool."
          value={feature.baseline_y}
          onChange={(v) => set('baseline_y', v)}
        />
      ) : null}
      {needsElevation ? (
        <PctInput label="How high it is" hint="How far above the floor it sits, as a part of the picture’s height (0–100)." value={feature.elevation} onChange={(v) => set('elevation', v)} />
      ) : null}
    </>
  )
}

function FacingSelect({ label, value, usual, onChange }: { label: string; value: Facing | undefined; usual?: Facing; onChange(v: Facing | undefined): void }) {
  return (
    <Field label={label}>
      <Select value={value ?? 'usual'} onValueChange={(v) => onChange(v === 'usual' ? undefined : (v as Facing))}>
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="usual">{usual ? `Usual (${FACING_LABEL[usual].toLowerCase()})` : 'Usual'}</SelectItem>
          {FACINGS.map((f) => (
            <SelectItem key={f} value={f}>
              {FACING_LABEL[f]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  )
}

function SpotSettings({ feature, kind }: { feature: StageBody['anchors'][number]; kind: KindDef | undefined }) {
  const sem = (kind?.semantics ?? {}) as { facing?: Facing; capacity?: number }
  const set = (field: string, v: unknown) => setFeatureFields(feature.id, { [field]: v }, `field:${feature.id}:${field}`)
  return (
    <>
      <FacingSelect label="Which way do they face here?" value={feature.facing} usual={sem.facing} onChange={(v) => set('facing', v)} />
      <Field label="How many can use it at once?" hint={`Leave empty for the usual (${sem.capacity ?? 1}).`}>
        <Input
          type="number"
          min={1}
          step={1}
          fieldSize="sm"
          value={feature.capacity === undefined ? '' : String(feature.capacity)}
          onChange={(e) => {
            const n = e.target.value === '' ? undefined : Math.round(Number(e.target.value))
            set('capacity', n === undefined || !Number.isFinite(n) || n < 1 ? undefined : n)
          }}
          onBlur={endEditSession}
        />
      </Field>
      {feature.stand ? (
        <Button variant="ghost" size="sm" className="w-fit" onClick={() => set('stand', undefined)}>
          Reset where characters stand
        </Button>
      ) : null}
    </>
  )
}

function WaySettings({ feature, kind, doc }: { feature: StageBody['portals'][number]; kind: KindDef | undefined; doc: StageBody }) {
  const vocab = useVocab()
  const sem = (kind?.semantics ?? {}) as { transition?: string; needs_anchor?: boolean }
  const where = feature.anchor_id ? `spot:${feature.anchor_id}` : feature.edge ? `edge:${feature.edge}` : ''
  const spotMissing = feature.anchor_id !== undefined && !doc.anchors.some((a) => a.id === feature.anchor_id)
  const set = (fields: Record<string, unknown>, key: string) => setFeatureFields(feature.id, fields, `field:${feature.id}:${key}`)
  return (
    <>
      <Field
        label="Where is it?"
        hint={
          sem.needs_anchor && !feature.anchor_id
            ? 'This kind belongs on a spot (like a door). Add the spot first if it is not in the list.'
            : 'An edge of the picture, or a spot on it.'
        }
      >
        <Select
          value={where}
          onValueChange={(v) => {
            if (v.startsWith('edge:')) set({ edge: v.slice(5), anchor_id: undefined }, 'where')
            else if (v.startsWith('spot:')) set({ anchor_id: v.slice(5), edge: undefined }, 'where')
          }}
        >
          <SelectTrigger aria-label="Where is it?">
            <SelectValue placeholder="Choose where it is" />
          </SelectTrigger>
          <SelectContent>
            {PORTAL_EDGES.map((e) => (
              <SelectItem key={e} value={`edge:${e}`}>
                {EDGE_LABEL[e]}
              </SelectItem>
            ))}
            {doc.anchors.map((a) => (
              <SelectItem key={a.id} value={`spot:${a.id}`}>
                On a spot: {a.label ?? kindLabel(vocab, a.kind, 'anchor_kind')}
              </SelectItem>
            ))}
            {spotMissing ? <SelectItem value={where}>A spot that was deleted</SelectItem> : null}
          </SelectContent>
        </Select>
      </Field>

      <Field label="How do characters go through?">
        <Select value={feature.transition ?? 'usual'} onValueChange={(v) => set({ transition: v === 'usual' ? undefined : v }, 'transition')}>
          <SelectTrigger aria-label="How do characters go through?">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="usual">Usual{sem.transition ? ` (${(TRANSITION_LABEL[sem.transition] ?? sem.transition).toLowerCase()})` : ''}</SelectItem>
            {Object.entries(TRANSITION_LABEL).map(([v, label]) => (
              <SelectItem key={v} value={v}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Row label="Characters can come in here as well as go out">
        <Toggle pressed={feature.bidirectional !== false} onPressedChange={(v) => set({ bidirectional: v ? undefined : false }, 'bidirectional')} aria-label="Both ways">
          {feature.bidirectional !== false ? 'Both ways' : 'Out only'}
        </Toggle>
      </Row>

      <FacingSelect label="Which way they face when they come in" value={feature.enter_facing} onChange={(v) => set({ enter_facing: v }, 'enter_facing')} />
    </>
  )
}

function PathSettings({ feature }: { feature: StageBody['paths'][number] }) {
  return (
    <Row label="It goes round in a loop (back to the start)">
      <Toggle pressed={feature.closed === true} onPressedChange={(v) => setFeatureFields(feature.id, { closed: v ? true : undefined }, `field:${feature.id}:closed`)} aria-label="Loop">
        {feature.closed ? 'Loop' : 'One way along'}
      </Toggle>
    </Row>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <span className="min-w-0 break-words text-sm font-medium text-ink">{label}</span>
      <span className="shrink-0">{children}</span>
    </div>
  )
}

/**
 * "Turn it into …": the other kinds of thing this can become (a shape → a spot; a spot → a shape
 * or a way in/out; a way in/out → a spot). Each asks which kind first; the change is one undo step
 * and, like every edit here, is only kept once you press Save.
 */
function ChangeTo({ ids }: { ids: readonly string[] }) {
  const targets = useChangeTargets(ids)
  if (targets.length === 0) return null
  const many = ids.length > 1
  return (
    <div className="-mt-1 flex min-w-0 flex-wrap items-center gap-1.5" data-testid="change-to" role="group" aria-label={many ? 'Turn them into' : 'Turn it into'}>
      <span className="text-xs font-medium text-ink-2">{many ? 'Turn them into' : 'Turn it into'}</span>
      {targets.map((to) => {
        const Icon = TARGET_ICON[to]
        return (
          <Button
            key={to}
            variant="outline"
            size="sm"
            className="h-8 min-w-0 max-w-full px-2.5 text-xs"
            title={TARGET_HINT[to]}
            data-change-to={to}
            onClick={() => openChangeTo(to as StageDomain, ids)}
          >
            <Icon aria-hidden />
            <span className="truncate">{targetLabel(to, many)}</span>
          </Button>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The kind's own fields → `props`                                              */
/* -------------------------------------------------------------------------- */

/**
 * One `KindDef.fields` entry as a control. The value lives in the feature's `props` bag; an empty
 * control removes the key (the kind's `default` is shown as the placeholder).
 */
export function KindField({ field, value, onChange }: { field: FieldDef; value: string | number | boolean | undefined; onChange: (v: string | number | boolean | undefined) => void }) {
  const label = field.label || field.key
  const placeholder = field.default !== undefined && field.default !== null ? String(field.default) : undefined
  switch (field.type) {
    case 'boolean': {
      const on = value === undefined ? field.default === true : value === true
      return (
        <Row label={label}>
          <Toggle pressed={on} onPressedChange={(v) => onChange(v)} aria-label={label}>
            {on ? 'Yes' : 'No'}
          </Toggle>
        </Row>
      )
    }
    case 'number':
      return (
        <Field label={label}>
          <Input
            type="number"
            min={field.min}
            max={field.max}
            fieldSize="sm"
            placeholder={placeholder}
            value={value === undefined ? '' : String(value)}
            onChange={(e) => {
              if (e.target.value === '') return onChange(undefined)
              const n = Number(e.target.value)
              if (Number.isFinite(n)) onChange(n)
            }}
            onBlur={endEditSession}
          />
        </Field>
      )
    case 'select': {
      const options = field.options ?? []
      if (options.length === 0) {
        return (
          <Field label={label}>
            <Input fieldSize="sm" value={value === undefined ? '' : String(value)} placeholder={placeholder} onChange={(e) => onChange(e.target.value || undefined)} onBlur={endEditSession} />
          </Field>
        )
      }
      return (
        <Field label={label}>
          <Select value={value === undefined ? '__unset__' : String(value)} onValueChange={(v) => onChange(v === '__unset__' ? undefined : v)}>
            <SelectTrigger aria-label={label}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__unset__">{placeholder ? `Usual (${options.find((o) => o.value === placeholder)?.label ?? placeholder})` : 'Not set'}</SelectItem>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )
    }
    default:
      return (
        <Field label={label} hint={field.type === 'asset_ref' ? 'A picture’s id, exactly as the library shows it.' : undefined}>
          <Input
            fieldSize="sm"
            value={value === undefined ? '' : String(value)}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
            onBlur={endEditSession}
          />
        </Field>
      )
  }
}

